/**
 * F-015 — Side panel showing the next N planets.
 *
 * Renders peeked tier indices as colored circles with size proportional
 * to tier radius. Subscribes externally — caller invokes refresh() after
 * advance() so the visual stays in sync.
 */

import { getTier } from "../game/tier.ts";
import type { PlanetQueue } from "../game/planet-queue.ts";
import { t } from "../i18n/index.ts";

export interface QueueStrip {
  element: HTMLDivElement;
  refresh(): void;
  detach(): void;
}

export function createQueueStrip(
  queue: PlanetQueue,
  host: HTMLElement,
): QueueStrip {
  const element = document.createElement("div");
  element.dataset["testid"] = "queue-strip";
  // Mobile-first: tighter margin + tighter padding at narrow widths.
  element.className =
    "pointer-events-none absolute left-0 top-1/2 m-3 flex -translate-y-1/2 flex-col items-center gap-2 rounded-2xl bg-black/40 px-2 py-2 backdrop-blur-sm sm:m-6 sm:gap-3 sm:px-3 sm:py-4";

  const label = document.createElement("div");
  label.className = "text-[10px] tracking-widest text-slate-300/70";
  label.textContent = t("queue.next");
  element.appendChild(label);

  const dotHost = document.createElement("div");
  dotHost.dataset["testid"] = "queue-dots";
  dotHost.className = "flex flex-col items-center gap-3";
  element.appendChild(dotHost);

  host.appendChild(element);

  const refresh = (): void => {
    dotHost.replaceChildren();
    const tiers = queue.peek();
    tiers.forEach((tierIndex, i) => {
      const tier = getTier(tierIndex);
      const node = document.createElement("div");
      node.dataset["testid"] = `queue-dot-${i}`;
      const px = Math.round(16 + tier.radius * 6);
      node.style.width = `${px}px`;
      node.style.height = `${px}px`;
      node.style.background = `#${tier.color.toString(16).padStart(6, "0")}`;
      node.style.opacity = i === 0 ? "1" : `${1 - i * 0.25}`;
      node.className = "rounded-full shadow-md";
      dotHost.appendChild(node);
    });
  };

  refresh();

  const detach = (): void => {
    element.remove();
  };

  return { element, refresh, detach };
}
